import React, { useState, useEffect, useRef } from "react";
import axiosInstance from "../../utils/axiosInstance";
import { API_ENDPOINTS } from "../../constants/config";
import { getStoredAuth, clearStoredAuth, setStoredAuth, getToken } from "../../utils/authStorage";

const VehicleDashboard = () => {
  const [vehicle, setVehicle] = useState(() => {
    const { vehicle: v } = getStoredAuth();
    return v || null;
  });
  const [sharing, setSharing] = useState(vehicle?.locationSharingActive || false);
  const [error, setError] = useState("");
  const intervalRef = useRef(null);

  // Sync vehicle state with storage changes (e.g., after login)
  useEffect(() => {
    const syncVehicle = () => {
      const { vehicle: v } = getStoredAuth();
      setVehicle(v || null);
      setSharing(v?.locationSharingActive || false);
    };
    window.addEventListener("storage", syncVehicle);
    return () => window.removeEventListener("storage", syncVehicle);
  }, []);

  const startLocationInterval = () => {
    if (intervalRef.current) return; // already running
    
    intervalRef.current = setInterval(() => {
      if (!navigator.geolocation) return;
      navigator.geolocation.getCurrentPosition(async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          await axiosInstance.post(API_ENDPOINTS.vehiclesStartLocation, {
            latitude,
            longitude,
          });
        } catch (err) {
          // Silently fail - location updates are non-critical
        }
      });
    }, 5000);
  };

  const stopLocationInterval = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const handleStart = () => {
    setError("");
    if (!vehicle || !vehicle.vehicleNumber) {
      setError("Vehicle not found in local storage. Please log in again.");
      clearStoredAuth();
      setVehicle(null);
      setSharing(false);
      return;
    }
    if (!navigator.geolocation) {
      alert("Geolocation not supported");
      return;
    }
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude, longitude } = pos.coords;
      try {
        const res = await axiosInstance.post(API_ENDPOINTS.vehiclesStartLocation, {
          latitude,
          longitude,
        });
      
        if (res.data.status === "success") {
          setSharing(true);
          setVehicle(res.data.vehicle);
            setStoredAuth({ token: getToken(), vehicle: res.data.vehicle, keepLoggedIn: !!localStorage.getItem("token") });
        startLocationInterval();
        } else {
          setError(res.data.message || "Failed to start location sharing.");
        }
      } catch (err) {
        setError(
          err.response?.data?.message ||
            "Failed to start location sharing. Please try again."
        );
        if (
          err.response?.data?.message &&
          err.response.data.message.toLowerCase().includes("user no longer exists")
        ) {
          clearStoredAuth();
          setVehicle(null);
          setSharing(false);
        }
      }
    });
  };

  const handleEnd = async () => {
    setError("");
    
    if (!vehicle || !vehicle.vehicleNumber) {
      setError("Vehicle not found in local storage. Please log in again.");
      clearStoredAuth();
      setVehicle(null);
      setSharing(false);
      stopLocationInterval();
      return;
    }
    
    try {
      const res = await axiosInstance.post(API_ENDPOINTS.vehiclesEndLocation, {
        vehicleNumber: vehicle.vehicleNumber,
      });
      
      if (res.data.status === "success") {
        setSharing(false);
        setVehicle(res.data.vehicle);
        setStoredAuth({ token: getToken(), vehicle: res.data.vehicle, keepLoggedIn: !!localStorage.getItem("token") });
        stopLocationInterval();
      } else if (
        res.data.message &&
        res.data.message.toLowerCase().includes("user no longer exists")
      ) {
        setError("Vehicle no longer exists. Please contact support.");
        clearStoredAuth();
        setVehicle(null);
        setSharing(false);
        stopLocationInterval();
      } else {
        setError(res.data.message || "Failed to end location sharing.");
      }
    } catch (err) {
      setError(
        err.response?.data?.message ||
          "Failed to end location sharing. Please try again."
      );
      if (
        err.response?.data?.message &&
        err.response.data.message.toLowerCase().includes("user no longer exists")
      ) {
        clearStoredAuth();
        setVehicle(null);
        setSharing(false);
        stopLocationInterval(); // Stop interval here
      }
    }
  };

  useEffect(() => {
    return () => stopLocationInterval(); // cleanup on unmount
  }, []);

  if (!vehicle) {
    return (
      <div className="max-w-md mx-auto mt-10 p-6 bg-white rounded shadow">
        Not logged in as a vehicle.
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto mt-10 p-6 bg-white rounded shadow">
      <h2 className="text-xl font-bold mb-4">Vehicle Dashboard</h2>
      {error && (
        <div className="mb-2 text-red-600 font-semibold">{error}</div>
      )}
      <div className="mb-2">
        <span className="font-medium">Vehicle Number:</span> {vehicle.vehicleNumber}
      </div>
      <div className="mb-2">
        <span className="font-medium">Type:</span>{" "}
        {vehicle.type?.charAt(0).toUpperCase() + vehicle.type?.slice(1)}
      </div>
      <div className="mt-4 flex gap-2">
        <button
          className="bg-green-600 text-white px-4 py-2 rounded"
          onClick={handleStart}
          disabled={sharing}
        >
          Start Location Sharing
        </button>
        <button
          className="bg-red-600 text-white px-4 py-2 rounded"
          onClick={handleEnd}
          disabled={!sharing}
        >
          End Location Sharing
        </button>
      </div>
      <div className="mt-2">
        Status: {sharing ? "Sharing Location" : "Not Sharing"}
      </div>
    </div>
  );
};

export default VehicleDashboard;